import pluralize from 'pluralize';
import * as React from 'react';
import { Dialog, Divider } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import fonts from 'styles/typography.module.css';

import { Customer } from 'types/Customer';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assignCustomer,
  AssignCustomerTarget,
  AssignmentMethod,
  getWatchListCount,
} from 'services/clients/watchList';
import { SelectionModel, useSelectionCount } from 'common/contexts/SelectionContext';
import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { WATCH_LIST_QUERY_PARAMS } from 'pages/TrackAndTrace/constants';
import { AssignTag } from '../AssignTag';
import { OverwriteWarning } from './OverwriteWarning';

interface Props {
  selection: SelectionModel;
  initialCustomer?: Customer;
  open: boolean;
  onClose: () => void;
  method: AssignmentMethod;
}

export const AssignmentModal: React.FC<Props> = (props) => {
  const { initialCustomer, open, onClose, selection, method } = props;
  const { searchParams } = useCustomSearchParams(WATCH_LIST_QUERY_PARAMS);

  const count = useSelectionCount(selection);

  const [customer, setCustomer] = React.useState<Customer | null>(initialCustomer || null);

  const watchListCountQuery = useQuery(['watchListCount', searchParams], ({ signal }) =>
    getWatchListCount({ filters: searchParams, signal })
  );

  const overwriteCount = React.useMemo(() => {
    if (selection.model === 'page') {
      return selection.containers.filter((c) => c.customer !== null).length;
    }

    if (selection.model === 'filter' && watchListCountQuery.data) {
      const { customerCounts } = watchListCountQuery.data;

      let sum = 0;
      customerCounts
        .filter((c) => c.customerCode !== null)
        .forEach((c) => {
          sum += c.count;
        });
      return sum;
    }

    return 0;
  }, [selection, watchListCountQuery.data]);

  const queryClient = useQueryClient();

  const mutation = useMutation(assignCustomer, {
    mutationKey: ['assignCustomer'],
    // This is a very generalized invalidation; it would be better to invalidate based on the current view but this is quicker and more full proof
    onSuccess: () => {
      queryClient.invalidateQueries(['watchList']);
      queryClient.invalidateQueries(['customers']);
    },
  });

  const handleSelect = (c: Customer | null) => {
    setCustomer(c);
  };

  const handleSave = () => {
    const target: AssignCustomerTarget = {};
    switch (selection.model) {
      case 'filter':
        target.view = searchParams;
        break;
      case 'page':
        target.containers = selection.containers;
        break;
      default:
        break;
    }
    mutation.mutateAsync({ target, customer, method }).then(() => {
      onClose();
    });
  };

  const [assignmentIsOpen, setAssignmentIsOpen] = React.useState<boolean>(false);
  const disabled = customer === initialCustomer || mutation.isLoading || assignmentIsOpen;

  return (
    <Dialog open={open} onClose={onClose}>
      <div className={fonts.font}>
        <div className="flex items-center p-4 justify-between">
          <h2 className="font-medium ml-4">
            Tag{' '}
            {count === 1 && selection.model === 'page'
              ? selection.containers[0].containerNumber
              : `${count} ${pluralize('container', count)}`}{' '}
            with a customer ID
          </h2>
          <div onClick={onClose} className="cursor-pointer">
            <CloseIcon />
          </div>
        </div>
        <Divider />
        <div className="p-8 pb-4 space-y-8">
          <AssignTag
            initialCustomer={initialCustomer}
            onSelect={handleSelect}
            onIsOpen={setAssignmentIsOpen}
          />
          {count > 1 && overwriteCount > 0 && <OverwriteWarning overwriteCount={overwriteCount} />}
        </div>
        <Divider />
        <div className="p-4 flex justify-end space-x-2 items-center">
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          {mutation.isError && <h4 className="text-red-900">{mutation.error.message}</h4>}
          <LoadingButton
            variant="contained"
            color="secondary"
            size="large"
            disabled={disabled}
            onClick={handleSave}
            loading={mutation.isLoading}
          >
            <h3>Save changes</h3>
          </LoadingButton>
        </div>
      </div>
    </Dialog>
  );
};
AssignmentModal.displayName = 'AssignmentModal';
